import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from '@/components/Select';
import { apiPutRequest } from '@/utils/apiClient';
import { useUser } from '@/hooks/UserContext';
import { resolveSupportedLanguage } from '@/utils/locale';

const LanguagePreference = (): ReactElement | null => {
    const { userInfo } = useUser();
    const { t } = useTranslation('settings');

    const [selectedLanguage, setSelectedLanguage] = useState<string>(() =>
        resolveSupportedLanguage(userInfo?.language),
    );

    useEffect(() => {
        setSelectedLanguage(resolveSupportedLanguage(userInfo?.language));
    }, [userInfo?.language]);

    const languageOptions = useMemo(
        () => [
            { value: 'ja', label: t('language.options.ja') },
            { value: 'en', label: t('language.options.en') },
        ],
        [t],
    );

    const selectedOption = useMemo(() => {
        const fallback = languageOptions[0] ?? { value: '', label: '' };
        return languageOptions.find((option) => option.value === selectedLanguage) ?? fallback;
    }, [languageOptions, selectedLanguage]);

    const saveLanguage = async (language: string) => {
        try {
            const response = await apiPutRequest('/api/settings/language', { language });
            if (!response.ok) throw new Error(`Failed to update language: ${response.status}`);
            // 変更を即時反映するためにページを再読み込み
            window.location.reload();
        } catch (error) {
            console.error(error);
        }
    };

    const handleLanguageChange = async (option: { value: string }) => {
        setSelectedLanguage(option.value);
        // 自動保存
        if (option.value && option.value !== resolveSupportedLanguage(userInfo?.language)) {
            await saveLanguage(option.value);
        }
    };

    return (
        <section className="flex flex-col gap-3 pt-6">
            <h3 className="text-lg font-medium">{t('language.heading')}</h3>
            <p className="text-sm text-gray-600">{t('language.description')}</p>
            <div className="max-w-xs">
                <Select
                    options={languageOptions}
                    defaultValue={selectedOption}
                    onValueChange={handleLanguageChange}
                />
            </div>
            {/* 成功/進行テキストの表示は不要のため削除 */}
        </section>
    );
};

export default LanguagePreference;
